import { Button, ButtonVariant, ChangeEvent, Label, LayoutProps, Panel, PermissionsDefinition } from "@mcleod/components";
import { ArrayUtil } from "@mcleod/core";
import { ModelPermsAllowDeny, RowPermsAllowDeny } from "../models/ModelPermsAllowDeny";
import { RowPermsSecureItem } from "../models/ModelPermsSecureItem";
import { PanelPermsGrant } from "./PanelPermsGrant";
import { AutogenLayoutPanelIndividualSecure } from "./autogen/AutogenLayoutPanelIndividualSecure";

export interface PanelIndividualSecureProps extends LayoutProps {
    permsGrant: PanelPermsGrant;
    secureItem: RowPermsSecureItem;
    def: PermissionsDefinition;
    pageIdentifier: string;
    itemName: string;
}

type UserOrGroup = "U" | "G";

interface AllowListItem {
    row: RowPermsAllowDeny;
}

export class PanelIndividualSecure extends AutogenLayoutPanelIndividualSecure implements PanelIndividualSecureProps {
    secureItem: RowPermsSecureItem;
    permsGrant: PanelPermsGrant;
    def: PermissionsDefinition;
    pageIdentifier: string;
    itemName: string;
    private allows: RowPermsAllowDeny[];

    onLoad() {
        this.loadAllows();
        this.updateUI(true);
        this.labelSecurityType.caption = this.def.description;
    }

    updateUI(duringLoad: boolean = false, deleting: boolean = false) {
        const secured = this.secureItem != null && !deleting;
        const someAllowed = this.allows?.length > 0;
        const availableToSomeDescription = this.def.availableToSomeDescription || (this.def.availableToNoneDescription + " except:");
        this.switchSecured.checked = secured;
        this.panelAllowed.visible = secured;
        this.imageSwitchLeft.name = secured ? null : "threePeople";
        this.imageSwitchRight.name = secured ? (someAllowed ? "threePeopleBlueMiddle" : "threePeople") : null;
        this.labelDescription.caption = secured ? (someAllowed ? availableToSomeDescription : this.def.availableToNoneDescription) : this.def.availableToAllDescription;
        if (this.secureItem != null)
            this.secureItem.set({ allow_count: this.allows?.length || 0 });
        if (!duringLoad)
            this.permsGrant.childChanged(this.secureItem, deleting);
        this.fillUserList();
    }

    fillUserList() {
        let items = [];
        if (this.permsGrant.userGroups == null)
            return;
        for (const group of Object.values(this.permsGrant.userGroups))
            if (!this.alreadyAllowed(group.get("id"), "G")) {
                const descr = group.get("descr") || group.get("id");
                const label = new Label({ caption: descr, imageName: "add" });
                label.addClickListener(() => this.addAllowed(group.get("id"), "G"));
                items.push(label);
            }
        this.permsGrant.listGroups.items = items;
        items = [];
        for (const user of Object.values(this.permsGrant.users))
            if (!this.alreadyAllowed(user.get("id"), "U")) {
                const descr = user.get("name") || user.get("id");
                const label = new Label({ caption: descr, imageName: "add" });
                label.addClickListener(() => this.addAllowed(user.get("id"), "U"));
                items.push(label);
            }
        this.permsGrant.listUsers.items = items;
    }

    private alreadyAllowed(id: string, userOrGroup: UserOrGroup): boolean {
        if (this.allows != null)
            for (const item of this.allows)
                if (item.get("user_or_group_id") === id && item.get("user_or_group_flag") === userOrGroup)
                    return true;
        return false;
    }

    addAllowed(id: string, userOrGroup: UserOrGroup) {
        const row = new RowPermsAllowDeny();
        row.set({
            screen_class: this.pageIdentifier,
            item_name: this.itemName,
            security_type: this.def.permsType,
            user_or_group_id: id,
            user_or_group_flag: userOrGroup,
            grant_deny_flag: "G"
        });
        row.post();
        this.allows.push(row);
        this.panelAllowed.add(this.createAllowItem(row));
        this.updateUI();
    }

    switchSecuredOnChange(event: ChangeEvent) {
        if (!event.userInitiatedChange)
            return;
        this.permsGrant.selectedChild = this;
        if (event.newValue) {
            if (this.secureItem == null)
                this.secureItem = new RowPermsSecureItem();
            this.secureItem.set({
                screen_class: this.pageIdentifier,
                item_name: this.itemName,
                security_type: this.def.permsType,
            });
            this.secureItem.post();
            this.loadAllows();
            this.updateUI();
        }
        else if (this.secureItem != null && !event.newValue) {
            this.secureItem.delete();
            this.updateUI(false, true);
            this.secureItem = null;
            this.width = 348;
        }
    }

    loadAllows() {
        new ModelPermsAllowDeny().search({ item_name: this.itemName, security_type: this.def.permsType, screen_class: this.pageIdentifier }).then(rows => {
            this.panelAllowed.removeAll();
            for (const row of rows.modelRows) {
                if (row.get("grant_deny_flag") === "G")
                    this.panelAllowed.add(this.createAllowItem(row));
            }
            this.allows = rows.modelRows;
            this.updateUI();
        });
    }

    private getDisplayName(row: RowPermsAllowDeny): string {
        let result: string;
        if (row.get("user_or_group_flag") === "G")
            result = this.permsGrant.userGroups[row.get("user_or_group_id")]?.get("descr");
        else
            result = this.permsGrant.users[row.get("user_or_group_id")]?.get("name");
        return result || row.get("user_or_group_id");
    }

    createAllowItem(row: RowPermsAllowDeny) {
        const result = new Panel({ fillRow: true });
        const label = new Label({ caption: this.getDisplayName(row), rowBreak: false, fillRow: true, imageColor: "primary" });
        const buttonDelete = new Button({ imageName: "x", variant: ButtonVariant.round, color: "subtle.darker" });
        label.imageName = row.get("user_or_group_flag") === "U" ? "personSolid" : "threePeople";
        buttonDelete.addClickListener(() => {
            row.delete();
            ArrayUtil.removeFromArray(this.allows, row);
            this.panelAllowed.remove(result);
            this.updateUI();
            this.permsGrant.selectedChild = this;
        });
        result.add(label, buttonDelete);
        (result as unknown as AllowListItem).row = row;
        return result;
    }

    thisOnClicsk(event) {
        this.permsGrant.selectedChild = this;
    }
}
